/**
 * Presentation of a finished resultset action result according to its execTarget (#897).
 *
 * Query.vue executes the action exactly once. Its existing navigation points describe the
 * finished result (route + route state + popup content) and ask the presenter whether the
 * result was presented elsewhere (new tab / popup). For the same window (and missing/invalid
 * targets) the presenter declines and the unchanged previous code path runs.
 *
 * The presenter has no Vue/Quasar dependency; all side effects are injected.
 */
import {EXEC_TARGET, popupWindowConfig, resolveExecTarget} from './ActionTarget.js'
import {closeReservedWindow, navigateReservedWindow, navigateReservedWindowExternal, reserveResultWindow} from './ActionResultHandoff.js'

// deep link parameters that would trigger an action again in the new tab
export const ONE_SHOT_QUERY_PARAMS = Object.freeze(['ac', 'aac', 'ctxt', 'deleteAction'])

export const RESULT_KINDS = Object.freeze({
  QUERY: 'query',
  FORMULAR: 'formular',
  DASHBOARD: 'dashboard',
  WEBENTITY: 'webentity',
  INTERNAL_LINK: 'internalLink',
  URL: 'url',
  PLUGIN: 'plugin'
})

// route state marker of a result tab: the first history entry of that tab has no "back" entry,
// so components must not navigate back after saving (Formular.vue)
export const NEW_TAB_STATE_MARKER = 'actionResultNewTab'

// internal link targets whose real portal component can be shown inside the popup: these
// components take their context from props (the popup has no own route)
export const POPUP_INTERNAL_ROUTES = Object.freeze(['Query', 'Formular', 'Webentity'])

// popup frames for external pages: no session/auth tokens, no referrer. The page keeps its own
// origin only when it is really foreign (a same-origin page could otherwise reach the portal and
// its session through the sandbox).
export const EXTERNAL_FRAME_SANDBOX = 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads'

let popupCounter = 0

export function withoutOneShotParams(query) {
  const result = {}
  Object.keys(query || {}).forEach(name => {
    if (!ONE_SHOT_QUERY_PARAMS.includes(name)) {
      result[name] = query[name]
    }
  })
  return result
}

/**
 * Popup formulars cannot use the route query: parameters of a formular:// result are passed as
 * prefill values like the existing formular popups do; deep link prefixes "qp." are removed.
 */
export function formularPrefillFromQuery(query) {
  const result = {}
  Object.keys(query || {}).forEach(name => {
    result[name.startsWith('qp.') ? name.substring(3) : name] = query[name]
  })
  return result
}

/**
 * Popup formular of a formular link action: Formular.vue prefills a field from selectedRow when
 * that value is truthy, otherwise from queryParameter (both matched case-insensitive). The popup
 * Formular only receives one prefill object, so the same priority is applied here.
 */
export function mergeFormularPrefill(selectedRow, queryParameter) {
  const result = {...(selectedRow || {})}
  const filled = new Set(Object.keys(result).filter(key => result[key]).map(key => key.toLowerCase()))
  Object.keys(queryParameter || {}).forEach(key => {
    const lower = key.toLowerCase()
    if (!filled.has(lower)) {
      // an empty row value of the same name must not shadow the parameter after lower-casing
      Object.keys(result).filter(rowKey => rowKey !== key && rowKey.toLowerCase() === lower).forEach(rowKey => delete result[rowKey])
      result[key] = queryParameter[key]
    }
  })
  return result
}

/**
 * Popup Query: parameter context of the target query. The same-window navigation passes
 * detailQueryKeyValue as route state and the global qp.* parameters as route query (router
 * beforeEach -> authStore deep link parameters). The popup has no route of its own, so the qp.*
 * parameters are passed as detailQueryKeyValue entries (prefix removed; they win over equally
 * named entries like deep link parameters do). Other route parameters (page, ac ...) are not
 * taken over and the caller route/store is not changed.
 */
export function popupQueryKeyValue(paramList, globalQueryParam) {
  const result = []
  const qpNames = new Set()
  Object.keys(globalQueryParam || {}).forEach(name => {
    if (name.startsWith('qp.') && name.length > 3) {
      const plain = name.substring(3)
      qpNames.add(plain.toLowerCase())
      result.push({name: plain, value: globalQueryParam[name]})
    }
  })
  const list = Array.isArray(paramList) ? paramList : []
  list.forEach(param => {
    if (param && typeof param.name === 'string' && !qpNames.has(param.name.toLowerCase())) {
      result.push({name: param.name, value: param.value})
    }
  })
  return result
}

/**
 * Resolve an internal iPortal link (already checked by Query.isInternalAppLink) to the popup
 * content of its real portal component. Returns null when the route has no popup capable
 * component - the caller then reports that instead of changing the target silently.
 *
 * @param {string} link
 * @param {object} deps origin, resolveRoute(path, query) => {name, params} | null
 */
export function resolveInternalLinkPopup(link, {origin, resolveRoute}) {
  let url
  try {
    url = new URL(link, origin)
  } catch (e) {
    return null
  }
  if (url.origin !== new URL(origin).origin) {
    return null
  }
  // one-shot deep link parameters (ac, aac ...) would execute an action again
  const query = withoutOneShotParams(Object.fromEntries(url.searchParams.entries()))
  let route
  try {
    route = resolveRoute(url.pathname, query)
  } catch (e) {
    route = null
  }
  if (!route || !POPUP_INTERNAL_ROUTES.includes(route.name)) {
    return null
  }
  const params = route.params || {}

  switch (route.name) {
    case 'Query':
      // like a direct call of the link: qp.* deep link parameters. The selection key k is not
      // taken over - Query.vue would write it into the global deep link store of the caller.
      return {
        kind: RESULT_KINDS.QUERY,
        popup: {queryID: params.id, detailQueryKeyValue: popupQueryKeyValue([], query)}
      }
    case 'Formular': {
      // Formular.vue takes oiID / qp.oiID from the route query, qp.* prefill the fields
      const prefill = formularPrefillFromQuery(query)
      return {
        kind: RESULT_KINDS.FORMULAR,
        popup: {
          contentID: params.contentid,
          selectedRow: JSON.stringify(prefill),
          selectedMultiple: null,
          objInstanceID: prefill.oiID || null,
          readonly: false
        }
      }
    }
    default:
      return {kind: RESULT_KINDS.WEBENTITY, popup: {webentityID: params.id, wedata: null}}
  }
}

/**
 * Can an external http(s) result be shown in the popup frame? Mixed content (http page in an
 * https portal) is always blocked by the browser. CSP/X-Frame-Options of a foreign page cannot be
 * read by the portal (cross-origin); the frame shows the explicit alternative for that case.
 */
export function externalFramePolicy(href, portalOrigin) {
  let url
  try {
    url = new URL(href, portalOrigin)
  } catch (e) {
    return {embeddable: false, reason: 'unsupported', href: null}
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return {embeddable: false, reason: 'unsupported', href: null}
  }
  const portal = new URL(portalOrigin)
  if (portal.protocol === 'https:' && url.protocol === 'http:') {
    return {embeddable: false, reason: 'mixedContent', href: url.href}
  }
  const sameOrigin = url.origin === portal.origin
  return {
    embeddable: true,
    reason: null,
    href: url.href,
    sandbox: sameOrigin ? EXTERNAL_FRAME_SANDBOX : EXTERNAL_FRAME_SANDBOX + ' allow-same-origin'
  }
}

// route state key of a plugin view opened in a result tab (route 'Plugin', /plugin/:id)
export const PLUGIN_VIEW_STATE_KEY = 'actionResultPlugin'

const PLUGIN_TYPE_VUE = 4
const PLUGIN_TYPE_IFRAME = 6

function plainParams(queryParams) {
  return (Array.isArray(queryParams) ? queryParams : [])
    .filter(param => param && typeof param.name === 'string')
    .map(param => ({name: param.name, value: param.value}))
}

function jsonClone(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value))
}

/**
 * Plugin view of a VUE (4) / IFRAME (6) plugin for a new result tab: the same inputs the plugin
 * panel of the calling query passes (Query.vue), as JSON data for the one-time tab handoff. The new
 * tab renders the view once from this state - the calling tab does not render it, the plugin is
 * not executed twice. Returns null when the view cannot be described (no view, unknown component,
 * not serializable).
 *
 * @param {object} view {type, frameLinkSrc, component, queryID, queryParams, queryColumns,
 *   queryResult, selected, queryMeta, setupToken}
 */
export function pluginViewState(view) {
  if (!view) return null
  try {
    if (view.type === PLUGIN_TYPE_IFRAME) {
      if (typeof view.frameLinkSrc !== 'string' || !view.frameLinkSrc) return null
      return {type: PLUGIN_TYPE_IFRAME, frameLinkSrc: view.frameLinkSrc, queryID: view.queryID || null, queryParams: plainParams(view.queryParams)}
    }
    if (view.type === PLUGIN_TYPE_VUE) {
      if (typeof view.component !== 'string' || !view.component) return null
      return {
        type: PLUGIN_TYPE_VUE,
        component: view.component,
        props: jsonClone({
          queryID: view.queryID,
          queryColumns: view.queryColumns,
          queryResult: view.queryResult,
          setupToken: view.setupToken,
          selected: view.selected,
          queryParams: view.queryParams,
          queryMeta: view.queryMeta
        })
      }
    }
  } catch (e) {
    return null // e.g. circular data
  }
  return null
}

function isFrameSource(href, origin) {
  try {
    const url = new URL(href, origin)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (e) {
    return false
  }
}

/**
 * Validate the plugin view state in the result tab (Plugin.vue). Only registered query plugin
 * components and http(s)/relative frame sources are rendered.
 *
 * @param {object} state value of PLUGIN_VIEW_STATE_KEY from history.state
 * @param {object} registry component name -> component (query plugin panel components)
 * @param {string} origin portal origin
 * @return {{frameLinkSrc, queryID, queryParams} | {componentName, component, props} | null}
 */
export function resolvePluginView(state, registry, origin) {
  if (state === null || typeof state !== 'object' || Array.isArray(state)) return null
  if (state.type === PLUGIN_TYPE_IFRAME) {
    if (typeof state.frameLinkSrc !== 'string' || !isFrameSource(state.frameLinkSrc, origin)) return null
    return {frameLinkSrc: state.frameLinkSrc, queryID: typeof state.queryID === 'string' ? state.queryID : null, queryParams: plainParams(state.queryParams)}
  }
  if (state.type === PLUGIN_TYPE_VUE) {
    if (typeof state.component !== 'string' || !registry || !Object.prototype.hasOwnProperty.call(registry, state.component)) return null
    const props = state.props !== null && typeof state.props === 'object' ? state.props : {}
    return {
      componentName: state.component,
      component: registry[state.component],
      // own page, not embedded in a query; missing arrays like an empty query
      props: {...props, queryColumns: props.queryColumns || [], queryResult: props.queryResult || [], selected: props.selected || [], embedded: false}
    }
  }
  return null
}

function isHttpUrl(href, origin) {
  try {
    const url = new URL(href, origin)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (e) {
    return false
  }
}

export class ActionResultPresenter {
  /**
   * @param {object} deps
   *   action      - the ResultSetAction
   *   win         - browser window (open, location.origin)
   *   resolveHref - (routeLocation) => href (router.resolve(...).href)
   *   openPopup   - (popupData) => void
   *   showError   - ({title, message, url}) => void
   *   t           - i18n function
   *   callerQueryID
   */
  constructor(deps) {
    this.action = deps.action
    this.win = deps.win
    this.resolveHref = deps.resolveHref
    this.openPopup = deps.openPopup
    this.showError = deps.showError
    this.t = deps.t || (key => key)
    this.callerQueryID = deps.callerQueryID
    this.onCloseButton = deps.onCloseButton
    this.target = resolveExecTarget(deps.action)
    this.reservedWindow = null
    this.reservedWindowUsed = false
    this.presentedCount = 0
  }

  /**
   * Start presenting an action inside the user interaction. For the new tab target the
   * window is reserved now (popup blocker); returns false when that failed - the action
   * must then NOT be executed (error was shown).
   */
  begin() {
    if (this.target !== EXEC_TARGET.NEW_TAB) {
      return true
    }
    this.reservedWindow = reserveResultWindow(this.win, this.t('actionResultNewTabLoading'))
    if (!this.reservedWindow) {
      this.showBlocked()
      return false
    }
    return true
  }

  /** Close a reserved but unused window (no view result, error, cancel). */
  end() {
    if (this.reservedWindow && !this.reservedWindowUsed) {
      closeReservedWindow(this.reservedWindow)
    }
    this.reservedWindow = null
  }

  get handlesResults() {
    return this.target === EXEC_TARGET.NEW_TAB || this.target === EXEC_TARGET.POPUP_DIALOG
  }

  /**
   * @param {object} result {kind, route, state, popup, url}
   * @return {boolean} true when presented (or a concrete error was shown) - the caller must
   *   then skip its same-window path; false for the same window target.
   */
  present(result) {
    if (!this.handlesResults || !result) {
      return false
    }
    this.presentedCount++
    if (this.target === EXEC_TARGET.NEW_TAB) {
      this.presentInNewTab(result)
    } else {
      this.presentInPopup(result)
    }
    return true
  }

  takeWindow() {
    if (this.reservedWindow && !this.reservedWindowUsed && !this.reservedWindow.closed) {
      this.reservedWindowUsed = true
      return this.reservedWindow
    }
    // further results of the same execution (e.g. several selected rows): still a user
    // initiated execution, try to open another tab - no silent same-tab fallback
    const another = reserveResultWindow(this.win, this.t('actionResultNewTabLoading'))
    if (!another) {
      this.showBlocked('actionResultNewTabBlockedResultMsg')
    }
    return another
  }

  presentInNewTab(result) {
    const origin = this.win.location.origin
    const reserved = this.takeWindow()
    if (!reserved) {
      return
    }
    try {
      if (result.kind === RESULT_KINDS.URL) {
        if (!isHttpUrl(result.url, origin)) {
          throw new Error('unsupported url')
        }
        navigateReservedWindowExternal(reserved, new URL(result.url, origin).href)

      } else if (result.kind === RESULT_KINDS.INTERNAL_LINK) {
        navigateReservedWindow(reserved, result.url, {[NEW_TAB_STATE_MARKER]: true}, {origin})

      } else if (result.kind === RESULT_KINDS.PLUGIN && !result.route) {
        // no describable plugin view (e.g. component not registered for the plugin page)
        closeReservedWindow(reserved)
        this.showNotPresentable(result, 'actionResultNewTabErrorTitle', 'actionResultNewTabPluginMsg')

      } else {
        // portal route of the result (plugin views: route 'Plugin' with the view as route state)
        const route = {...result.route, query: withoutOneShotParams(result.route.query)}
        navigateReservedWindow(reserved, this.resolveHref(route), {...result.state, [NEW_TAB_STATE_MARKER]: true}, {origin})
      }
    } catch (e) {
      closeReservedWindow(reserved)
      this.showError({title: this.t('actionResultNewTabErrorTitle'), message: this.t('actionResultNewTabErrorMsg')})
    }
  }

  presentInPopup(result) {
    const resolved = result.resolved && result.resolved.kind && result.resolved.popup ? result.resolved : null
    if ((result.kind === RESULT_KINDS.URL || result.kind === RESULT_KINDS.INTERNAL_LINK) && resolved) {
      // link to a portal route: its real portal component instead of a frame / an error
      this.openResultPopup(resolved.kind, resolved.popup)
      return
    }
    if (result.kind === RESULT_KINDS.URL) {
      const policy = externalFramePolicy(result.url, this.win.location.origin)
      if (!policy.embeddable) {
        this.showError({
          title: this.t('actionResultPopupErrorTitle'),
          message: this.t(policy.reason === 'mixedContent' ? 'actionResultPopupMixedContentMsg' : 'actionResultPopupExternalMsg'),
          url: policy.href
        })
        return
      }
      this.openResultPopup(RESULT_KINDS.URL, {url: policy.href, sandbox: policy.sandbox})
      return
    }
    if (result.kind === RESULT_KINDS.INTERNAL_LINK) {
      this.showError({title: this.t('actionResultPopupErrorTitle'), message: this.t('actionResultPopupInternalLinkMsg'), url: result.url})
      return
    }
    if (result.kind === RESULT_KINDS.PLUGIN && !result.popup) {
      this.showNotPresentable(result, 'actionResultPopupErrorTitle', 'actionResultPopupPluginMsg')
      return
    }
    this.openResultPopup(result.kind, result.popup)
  }

  openResultPopup(kind, popup) {
    popupCounter++
    const windowConfig = popupWindowConfig(this.action)
    this.openPopup({
      popupID: 'actionResult_' + (this.action && this.action.id ? this.action.id : '') + '_' + popupCounter,
      window: windowConfig,
      onCloseButton: this.onCloseButton,
      content: {...popup, kind, callerQueryID: this.callerQueryID, setupToken: windowConfig.setupToken}
    })
  }

  /** Result without presentation for this target: concrete message, previous display only on explicit choice. */
  showNotPresentable(result, titleKey, messageKey) {
    const error = {title: this.t(titleKey), message: this.t(messageKey)}
    if (typeof result.showInCurrentWindow === 'function') {
      error.alternative = {label: this.t('actionResultShowInCurrentWindow'), run: result.showInCurrentWindow}
    }
    this.showError(error)
  }

  showBlocked(messageKey = 'actionResultNewTabBlockedMsg') {
    this.showError({title: this.t('actionResultNewTabBlockedTitle'), message: this.t(messageKey)})
  }
}
